/**
 * Big Toe Energy — <bte-review-carousel> for the "Avis clients (BTE)" section.
 *
 * - Native horizontal scroll with snap points: swipe works without any library
 * - Several reviews per view; one dot per scroll position
 * - Arrows and dots only appear when there is something to scroll (not in the desktop grid)
 * - Autoplay pauses on hover, focus, touch, off screen and hidden tab, and stops for good
 *   once the visitor plays a video with sound
 * - Theme editor: selecting a review block scrolls to it
 */

class BteReviewCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLButtonElement[]} */
  #dots = [];
  #index = 0;
  #maxIndex = 0;
  #maxScroll = 0;
  #padStart = 0;
  #timer = 0;
  #frame = 0;
  #hovered = false;
  #focused = false;
  #visible = true;
  #stopped = false;
  /** @type {number | null} Position we are scrolling to with a button, dot or autoplay */
  #scrollTarget = null;
  #scrollTargetTimer = 0;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#track = this.querySelector('.bte-rs__track');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.children).filter((el) => el.classList.contains('bte-rs__slide'));
    const count = this.#slides.length;
    this.#slides.forEach((slide, i) => slide.setAttribute('aria-label', `${i + 1} / ${count}`));

    this.querySelectorAll('[data-bte-prev]').forEach((b) => b.addEventListener('click', this.#onPrev));
    this.querySelectorAll('[data-bte-next]').forEach((b) => b.addEventListener('click', this.#onNext));
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });

    this.addEventListener('pointerenter', this.#onEnter);
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('touchstart', this.#schedule, { passive: true });
    // Media events don't bubble: listen in the capture phase
    this.addEventListener('play', this.#onMedia, true);
    this.addEventListener('volumechange', this.#onMedia, true);
    document.addEventListener('visibilitychange', this.#schedule);
    document.addEventListener('shopify:block:select', this.#onEditorSelect);

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        this.#visible = entries.some((entry) => entry.isIntersecting);
        this.#schedule();
      });
      this.#observer.observe(this);
    }

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#measure());
      this.#resizeObserver.observe(this.#track);
    }

    this.#measure();
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#scrollTargetTimer);
    cancelAnimationFrame(this.#frame);
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#schedule);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
  }

  get #loops() {
    return this.dataset.type === 'loop';
  }

  get #scrollable() {
    return this.#maxScroll > 1;
  }

  /** Scroll position that shows slide `i` first (the last positions stop at the end of the track) */
  /** @param {number} i */
  #position(i) {
    const slide = this.#slides[i];
    if (!slide) return 0;
    return Math.min(Math.max(slide.offsetLeft - this.#padStart, 0), this.#maxScroll);
  }

  #measure = () => {
    const track = this.#track;
    if (!track) return;
    this.#padStart = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    this.#maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

    let maxIndex = 0;
    if (this.#scrollable) {
      maxIndex = this.#slides.length - 1;
      for (let i = 0; i < this.#slides.length; i++) {
        if (this.#slides[i].offsetLeft - this.#padStart >= this.#maxScroll - 1) {
          maxIndex = i;
          break;
        }
      }
    }
    this.#maxIndex = maxIndex;
    this.classList.toggle('is-scrollable', this.#scrollable);

    if (this.#dots.length !== maxIndex + 1) this.#buildDots();
    this.#setActive(Math.min(this.#closestIndex(), maxIndex));
    this.#schedule();
  };

  #buildDots() {
    const container = this.querySelector('[data-bte-dots]');
    if (!container) return;
    container.replaceChildren();
    const total = this.#maxIndex + 1;
    this.#dots = [];
    if (total < 2) return;
    for (let i = 0; i < total; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bte-rs__dot';
      dot.setAttribute('aria-label', `Go to review ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i));
      container.append(dot);
      this.#dots.push(dot);
    }
  }

  #closestIndex() {
    const left = this.#track?.scrollLeft || 0;
    if (left >= this.#maxScroll - 1) return this.#maxIndex;
    let closest = 0;
    let distance = Infinity;
    for (let i = 0; i <= this.#maxIndex; i++) {
      const d = Math.abs(this.#position(i) - left);
      if (d < distance) {
        distance = d;
        closest = i;
      }
    }
    return closest;
  }

  /** @param {number} index */
  goTo(index, { instant = false } = {}) {
    if (!this.#track || !this.#scrollable) return;
    const total = this.#maxIndex + 1;
    const target = this.#loops ? (index + total) % total : Math.min(Math.max(index, 0), this.#maxIndex);
    const smooth = !instant && !this.#reduceMotion;

    // Ignore the in-between positions while the smooth scroll passes over them
    this.#scrollTarget = smooth ? target : null;
    window.clearTimeout(this.#scrollTargetTimer);
    if (smooth) this.#scrollTargetTimer = window.setTimeout(() => (this.#scrollTarget = null), 1000);

    this.#track.scrollTo({ left: this.#position(target), behavior: smooth ? 'smooth' : 'auto' });
    this.#setActive(target);
    this.#schedule();
  }

  next() {
    // Autoplay always rewinds at the end, even in "classic" mode
    if (this.#index >= this.#maxIndex) this.goTo(0);
    else this.goTo(this.#index + 1);
  }

  /** @param {number} index */
  #setActive(index) {
    this.#index = index;
    this.#dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    const atStart = index === 0;
    const atEnd = index >= this.#maxIndex;
    this.querySelectorAll('[data-bte-prev]').forEach((b) => {
      if (b instanceof HTMLButtonElement) b.disabled = !this.#loops && atStart;
    });
    this.querySelectorAll('[data-bte-next]').forEach((b) => {
      if (b instanceof HTMLButtonElement) b.disabled = !this.#loops && atEnd;
    });
  }

  #schedule = () => {
    window.clearTimeout(this.#timer);
    if (this.dataset.autoplay !== 'true' || this.#reduceMotion || this.#stopped) return;
    if (!this.#scrollable || this.#hovered || this.#focused || !this.#visible || document.hidden) return;
    const delay = (parseFloat(this.dataset.autoplaySpeed || '5') || 5) * 1000;
    this.#timer = window.setTimeout(() => this.next(), delay);
  };

  #stop() {
    this.#stopped = true;
    window.clearTimeout(this.#timer);
  }

  #onPrev = () => {
    this.goTo(this.#index - 1);
  };

  #onNext = () => {
    this.goTo(this.#index + 1);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const closest = this.#closestIndex();
      if (this.#scrollTarget !== null) {
        if (closest === this.#scrollTarget) this.#scrollTarget = null;
        return;
      }
      if (closest !== this.#index) {
        this.#setActive(closest);
        this.#schedule();
      }
    });
  };

  /** A video playing with sound (or any video started by a click) stops the autoplay for good */
  /** @param {Event} event */
  #onMedia = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    if (event.type === 'play' && this.dataset.videoMode === 'click') this.#stop();
    else if (!video.muted && !video.paused) this.#stop();
  };

  /** @param {PointerEvent} event */
  #onEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#schedule();
  };

  #onLeave = () => {
    this.#hovered = false;
    this.#schedule();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#schedule();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#focused = false;
    this.#schedule();
  };

  /** @param {Event} event */
  #onEditorSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const index = this.#slides.findIndex((slide) => slide === target || slide.contains(target));
    if (index === -1) return;
    this.#stop();
    this.goTo(Math.min(index, this.#maxIndex), { instant: true });
  };
}

if (!customElements.get('bte-review-carousel')) customElements.define('bte-review-carousel', BteReviewCarousel);
